import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider, useLang } from "@/context/LanguageContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/Layout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import OrgChart from "@/pages/OrgChart";
import Guests from "@/pages/Guests";
import SharedMailboxes from "@/pages/SharedMailboxes";
import EmployeeDetail from "@/pages/EmployeeDetail";
import LeavedDetail from "@/pages/LeavedDetail";
import Onboard from "@/pages/Onboard";
import Groups from "@/pages/Groups";
import DistributionLists from "@/pages/DistributionLists";
import Licenses from "@/pages/Licenses";
import Teams from "@/pages/Teams";
import M365Groups from "@/pages/M365Groups";
import RoleTemplates from "@/pages/RoleTemplates";
import Applications from "@/pages/Applications";
import Companies from "@/pages/Companies";
import Actions from "@/pages/Actions";
import AiAssistant from "@/pages/AiAssistant";
import Settings from "@/pages/Settings";
import Admins from "@/pages/Admins";
import Preferences from "@/pages/Preferences";
import Setup from "@/pages/Setup";
import ResetPassword from "@/pages/ResetPassword";
import Jobs from "@/pages/Jobs";
import ImportExport from "@/pages/ImportExport";
import SuperAdmin from "@/pages/SuperAdmin";
import E2EHistory from "@/pages/E2EHistory";
import TenantNotFound from "@/pages/TenantNotFound";
import Expired from "@/pages/Expired";
import FrontendVersion from "@/pages/FrontendVersion";

function LangSync() {
  const { user } = useAuth();
  const { setLang } = useLang();
  useEffect(() => {
    if (user && user.preferredLanguage && !localStorage.getItem("lang")) {
      setLang(user.preferredLanguage, false);
    }
  }, [user, setLang]);
  return null;
}

const wrap = (el, adminOnly = false, perm = null, superOnly = false) => (
  <ProtectedRoute adminOnly={adminOnly} perm={perm} superOnly={superOnly}>
    <Layout>{el}</Layout>
  </ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <LangSync />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/version" element={<FrontendVersion />} />
              <Route path="/tenant-not-found" element={<TenantNotFound />} />
              <Route path="/expired" element={<Expired />} />
              <Route path="/setup" element={<Setup />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/" element={wrap(<Dashboard />)} />
              <Route path="/employees" element={wrap(<Employees />)} />
              <Route path="/org-chart" element={wrap(<OrgChart />)} />
              <Route path="/guests" element={wrap(<Guests />)} />
              <Route path="/shared-mailboxes" element={wrap(<SharedMailboxes />)} />
              <Route path="/employees/:id" element={wrap(<EmployeeDetail />)} />
              <Route path="/leaved/:id" element={wrap(<LeavedDetail />)} />
              <Route path="/onboard" element={wrap(<Onboard />, false, "onboarding")} />
              <Route path="/groups" element={wrap(<Groups />)} />
              <Route path="/distribution-lists" element={wrap(<DistributionLists />)} />
              <Route path="/licenses" element={wrap(<Licenses />)} />
              <Route path="/teams" element={wrap(<Teams />)} />
              <Route path="/m365-groups" element={wrap(<M365Groups />)} />
              <Route path="/role-templates" element={wrap(<RoleTemplates />)} />
              <Route path="/applications" element={wrap(<Applications />)} />
              <Route path="/companies" element={wrap(<Companies />)} />
              <Route path="/actions" element={wrap(<Actions />)} />
              <Route path="/ai-assistant" element={wrap(<AiAssistant />, false, "ai.use")} />
              <Route path="/jobs" element={wrap(<Jobs />)} />
              <Route path="/import-export" element={wrap(<ImportExport />, false, "employees.manage")} />
              <Route path="/preferences" element={wrap(<Preferences />)} />
              <Route path="/admins" element={wrap(<Admins />, true)} />
              <Route path="/settings" element={wrap(<Settings />, false, "settings.manage")} />
              <Route path="/superadmin" element={wrap(<SuperAdmin />, false, null, true)} />
              <Route path="/e2e-history" element={wrap(<E2EHistory />, true)} />
            </Routes>
            <Toaster position="bottom-right" />
          </AuthProvider>
        </LanguageProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
